import React, { useState } from 'react';
import styled from 'styled-components';
import { colors } from '@/constants/colors';
import { toast } from 'react-toastify';
import Input from '../UI/Input';
import Btn from '../UI/Btn';
import { updateRate, Rate } from '@/api/rate';
import { useUIStore } from '@/store/useUIStore';
import { priceFormat } from '@/utils/currencyFormatter';

interface Props {
   data: Rate;
}

const EditRate = ({ data }: Props) => {
   const { handleClose, handleRefresh } = useUIStore();
   const [title, setTitle] = useState(data.title || '');
   const [description, setDescription] = useState(data.description || '');
   const [isLoading, setIsLoading] = useState(false);

   const handleSave = async () => {
      if (isLoading) return;
      try {
         setIsLoading(true);
         await updateRate(data._id, { title, description });
         toast.success('Rate updated', { autoClose: 2000 });
         handleRefresh();
         handleClose();
      } catch (err: any) {
         toast.error(err?.response?.data?.err || 'Failed to update rate');
      } finally {
         setIsLoading(false);
      }
   };

   return (
      <Wrapper>
         <Summary>
            <Price>{priceFormat(data.price, true)}</Price>
            <StoreName>{data.storeName}</StoreName>
         </Summary>
         <Input
            label="Title"
            name="title"
            value={title}
            placeholder="e.g 3 Hrs Unlimited Internet"
            onChange={(e) => setTitle(e.target.value)}
         />
         <Label>Description</Label>
         <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g 5Mbps UL/DL · No expiration · Unlimited bandwidth"
         />
         <BtnRow>
            <div onClick={handleClose}>
               <Btn title="Cancel" type="outline" size="small" />
            </div>
            <div onClick={handleSave}>
               <Btn
                  title={isLoading ? 'Saving...' : 'Save'}
                  type={isLoading ? 'disabled' : 'normal'}
                  size="small"
                  bgcolor={isLoading ? colors.GRAY10 : colors.PRIMARY}
                  textcolor={isLoading ? colors.GRAY1 : colors.WHITE}
               />
            </div>
         </BtnRow>
      </Wrapper>
   );
};

const Wrapper = styled.div`
   width: 480px;
   max-width: 100%;
   display: flex;
   flex-direction: column;
   gap: 14px;
   @media (max-width: 768px) { width: 100%; }
`;
const Summary = styled.div`
   display: flex;
   align-items: baseline;
   gap: 10px;
   padding: 12px 14px;
   border-radius: 10px;
   background: var(--app-brand-tint);
`;
const Price = styled.span`
   font-size: 22px;
   font-weight: 800;
   color: ${colors.PRIMARY};
`;
const StoreName = styled.span`
   font-size: 13px;
   color: ${colors.GRAY4};
`;
const Label = styled.label`
   font-size: 13px;
   color: ${colors.GRAY1};
   font-weight: 600;
`;
const Textarea = styled.textarea`
   width: 100%;
   padding: 10px 12px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 6px;
   font-size: 14px;
   font-family: inherit;
   color: ${colors.GRAY_DARK};
   background: var(--app-surface);
   outline: none;
   resize: vertical;
   min-height: 80px;
   &:focus { border-color: ${colors.PRIMARY}; }
`;
const BtnRow = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: 10px;
   margin-top: 4px;
`;

export default EditRate;
